// The household page's "+ Add policy" form (Joy/Brandon-driven, no
// spreadsheet), moved out of HouseholdPage.tsx on 2026-10-02 so the Data
// Needed card for an unplaceable carrier row can open the SAME form,
// pre-filled from the row, instead of a second hand-copied one. Behaviour
// on the household page is unchanged: every new prop is optional and
// defaults to what the page did before.

import { useState } from "react";

import { api, ApiError, type HouseholdContact } from "../../api";
import { Button } from "../../ui";
import { DateField } from "../DateField";
import {
  DuplicatePolicyNumberWarning,
  readExistingPolicy,
  type ExistingPolicyRef,
} from "../service-records/DuplicatePolicyNumberWarning";
import { PersonSelect, defaultPersonSelection } from "./PersonSelect";
import {
  annualizePremiumForTerm,
  COMMON_TERM_LENGTHS_MONTHS,
  describeTermLength,
} from "../../../../shared/insurance/policy-term-premium";
import {
  INSURANCE_POLICY_TYPE_OPTIONS,
  policyTypeFromInsuranceServiceType,
} from "../../../../shared/insurance/policy-type-options";

function formatUsd(amount: number | null): string {
  if (amount === null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

/** What a caller already knows about the policy (the Data Needed card
 *  fills these from the carrier row). Every field stays editable. */
export interface AddInsurancePolicyInitial {
  carrier?: string | null;
  policyNumber?: string | null;
  notes?: string | null;
}

export interface AddInsurancePolicyCreated {
  service_record_id: string;
  insurance_policy_id: string;
}

// ─── Manual insurance policy add (Joy/Brandon-driven, no spreadsheet) ─
// INSURANCE_POLICY_TYPE_OPTIONS moved to shared/insurance/policy-type-options.ts
// (2026-08-12) so the record page's "Add policy details" card can share the
// same list instead of a second hand-copied one.

export function AddInsurancePolicyInlineForm({
  householdId,
  onSaved,
  ctaLabel = "+ Add policy",
  householdContacts = [],
  primaryContactId = null,
  onAddPerson,
  initial,
  startOpen = false,
  onCancel,
  onCreated,
  initialServiceType,
}: {
  householdId: string;
  onSaved: () => void;
  /** Collapsed-state button label. Defaults to "+ Add policy"; the
   *  not-served card uses "+ Add existing policy" to make it clear this
   *  records a policy the household ALREADY has (vs. starting a quote). */
  ctaLabel?: string;
  /** Members + primary for the "For whom?" picker (0232 — a record
   *  cannot exist without a person). */
  householdContacts?: HouseholdContact[];
  primaryContactId?: string | null;
  /** Jumps to the household's Members tab from the "For whom?" empty
   *  state — see PersonSelect. */
  onAddPerson?: () => void;
  /** Starting values (2026-10-02). Reset returns to these, not to blank. */
  initial?: AddInsurancePolicyInitial;
  /** Open the form at once, with no "+ Add policy" button first. */
  startOpen?: boolean;
  /** Called when Cancel is pressed (after the form resets). */
  onCancel?: () => void;
  /** Called with the new record's ids, before onSaved. */
  onCreated?: (created: AddInsurancePolicyCreated) => void | Promise<void>;
  /** The service_type of the Services Check row that opened this form
   *  ("auto_policy"); preselects the policy type (Ryan, 2026-10-28). */
  initialServiceType?: string;
}) {
  const startPolicyType =
    (initialServiceType ? policyTypeFromInsuranceServiceType(initialServiceType) : null) ?? "home";
  const [open, setOpen] = useState(startOpen);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Grant 2026-08-18 — the household already has this policy number.
  // Held rather than thrown so the form can name the existing record and
  // offer "Add it anyway".
  const [duplicateWarning, setDuplicateWarning] = useState<{
    message: string;
    existing: ExistingPolicyRef | null;
  } | null>(null);
  const [policyType, setPolicyType] = useState(startPolicyType);
  const [carrier, setCarrier] = useState(initial?.carrier ?? "");
  const [policyNumber, setPolicyNumber] = useState(initial?.policyNumber ?? "");
  const [effective, setEffective] = useState("");
  const [expiration, setExpiration] = useState("");
  // Term length select: "12" | "6" | "3" | "other" — 12 covers the vast
  // majority of the book, so it's the default rather than forcing a
  // choice every time (Grant, 2026-08-11: terms vary — 6-month auto is
  // common — and the OS needs to handle that as a normal case, not an
  // exception).
  const [termLengthChoice, setTermLengthChoice] = useState<string>("12");
  const [customTermLength, setCustomTermLength] = useState("");
  const [premium, setPremium] = useState("");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [forContactIds, setForContactIds] = useState<string[]>(() =>
    defaultPersonSelection(householdContacts, primaryContactId),
  );

  const termLengthMonths: number | null =
    termLengthChoice === "other"
      ? customTermLength.trim() === "" || !Number.isFinite(Number(customTermLength))
        ? null
        : Number(customTermLength)
      : Number(termLengthChoice);
  const termUnitLabel = describeTermLength(termLengthMonths);
  const isAnnualTerm = termLengthMonths === null || termLengthMonths === 12;

  function reset() {
    setPolicyType(startPolicyType);
    setCarrier(initial?.carrier ?? "");
    setPolicyNumber(initial?.policyNumber ?? "");
    setEffective("");
    setExpiration("");
    setTermLengthChoice("12");
    setCustomTermLength("");
    setPremium("");
    setNotes(initial?.notes ?? "");
    setForContactIds(defaultPersonSelection(householdContacts, primaryContactId));
    setError(null);
    setDuplicateWarning(null);
  }

  // Grant 2026-08-18 — the household already has this policy number.
  // Every hand-added policy in production was created after 2026-07-17,
  // and five of them duplicated a policy the carrier import had already
  // brought in, because the person adding it had no way to see it was
  // already there. The server refuses with 409 + the existing record;
  // this holds it so the form can NAME that record and offer "Add it
  // anyway" for the genuine case (a carrier reusing a number).
  async function handleSave(allowDuplicate = false) {
    setError(null);
    if (!allowDuplicate) setDuplicateWarning(null);
    if (!carrier.trim()) {
      setError("Carrier is required.");
      return;
    }
    if (termLengthChoice === "other" && termLengthMonths === null) {
      setError("Enter the term length in months.");
      return;
    }
    setSaving(true);
    try {
      const premiumNum = premium.trim() === "" ? null : Number(premium.replace(/[^0-9.]/g, ""));
      const created = await api.addInsurancePolicy(householdId, {
        policy_type: policyType,
        // Whose policy this is (0232) — required; Save is disabled
        // until at least one person is picked.
        for_contact_ids: forContactIds,
        carrier_display_name: carrier.trim(),
        policy_number: policyNumber.trim() || null,
        effective_date: effective || null,
        expiration_date: expiration || null,
        term_length_months: termLengthMonths,
        // Sent as the TERM premium — the server derives annual_premium
        // from term_length_months (identical to term_premium when the
        // term is 12 months, so this is a no-op change for the common
        // case and the fix for every other one).
        term_premium: premiumNum !== null && Number.isFinite(premiumNum) ? premiumNum : null,
        notes: notes.trim() || null,
        ...(allowDuplicate ? { allow_duplicate_policy_number: true } : {}),
      });
      reset();
      setOpen(false);
      await onCreated?.({
        service_record_id: created.service_record_id,
        insurance_policy_id: created.insurance_policy_id,
      });
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.code === "duplicate_policy_number") {
        setDuplicateWarning({
          message: e.message,
          existing: readExistingPolicy(e),
        });
      } else if (e instanceof ApiError) setError(`${e.status} — ${e.message}`);
      else if (e instanceof Error) setError(e.message);
      else setError("Unknown error");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <div style={{ marginTop: 8 }}>
        <Button
          variant="sw-default"
          onClick={() => setOpen(true)}
          style={{ fontSize: 12, padding: "4px 10px" }}
        >
          {ctaLabel}
        </Button>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 8,
        padding: 10,
        background: "var(--bg-1)",
        borderRadius: 8,
        border: "1px solid var(--mid-gray)",
      }}
    >
      <div
        style={{
          display: "grid",
          // minmax(140px, 1fr) — a fixed minimum instead of the implicit
          // content-based one — is what makes this collapse to a single
          // column instead of overflowing when the card is narrow (the
          // Overview tab's 5-across grid squeezes each card to a
          // fraction of the page; a plain "1fr 1fr" let a date input's
          // intrinsic width push the fields past the card's edge).
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 8,
        }}
      >
        <label style={{ fontSize: 11, color: "var(--fg-muted)", minWidth: 0 }}>
          Type
          <select
            value={policyType}
            onChange={(e) => setPolicyType(e.target.value)}
            disabled={saving}
            style={{ display: "block", width: "100%", marginTop: 2 }}
          >
            {INSURANCE_POLICY_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 11, color: "var(--fg-muted)", minWidth: 0 }}>
          Carrier *
          <input
            type="text"
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
            placeholder="e.g., Foremost, Travelers"
            disabled={saving}
            style={{ display: "block", width: "100%", marginTop: 2 }}
          />
        </label>
        <label style={{ fontSize: 11, color: "var(--fg-muted)", minWidth: 0 }}>
          Policy number
          <input
            type="text"
            value={policyNumber}
            onChange={(e) => setPolicyNumber(e.target.value)}
            disabled={saving}
            style={{ display: "block", width: "100%", marginTop: 2 }}
          />
        </label>
        <label style={{ fontSize: 11, color: "var(--fg-muted)", minWidth: 0 }}>
          Term length
          <select
            value={termLengthChoice}
            onChange={(e) => setTermLengthChoice(e.target.value)}
            disabled={saving}
            style={{ display: "block", width: "100%", marginTop: 2 }}
          >
            {COMMON_TERM_LENGTHS_MONTHS.map((m) => (
              <option key={m} value={String(m)}>
                {m} months
              </option>
            ))}
            <option value="other">Other…</option>
          </select>
        </label>
        {termLengthChoice === "other" && (
          <label style={{ fontSize: 11, color: "var(--fg-muted)", minWidth: 0 }}>
            Term length (months)
            <input
              type="text"
              inputMode="numeric"
              value={customTermLength}
              onChange={(e) => setCustomTermLength(e.target.value)}
              placeholder="e.g. 9"
              disabled={saving}
              style={{ display: "block", width: "100%", marginTop: 2 }}
            />
          </label>
        )}
        <label style={{ fontSize: 11, color: "var(--fg-muted)", minWidth: 0 }}>
          Premium ({termUnitLabel})
          <input
            type="text"
            value={premium}
            onChange={(e) => setPremium(e.target.value)}
            placeholder="$"
            disabled={saving}
            style={{ display: "block", width: "100%", marginTop: 2 }}
          />
          {!isAnnualTerm && premium.trim() !== "" && (
            <span style={{ display: "block", marginTop: 2 }}>
              ≈{" "}
              {formatUsd(
                annualizePremiumForTerm(
                  Number(premium.replace(/[^0-9.]/g, "")) || 0,
                  termLengthMonths,
                ),
              )}
              /yr
            </span>
          )}
        </label>
        <label style={{ fontSize: 11, color: "var(--fg-muted)", minWidth: 0 }}>
          Effective date
          <DateField
            value={effective}
            onChange={(v) => setEffective(v)}
            disabled={saving}
            style={{ display: "block", width: "100%", marginTop: 2 }}
          />
        </label>
        <label style={{ fontSize: 11, color: "var(--fg-muted)", minWidth: 0 }}>
          Expiration date
          <DateField
            value={expiration}
            onChange={(v) => setExpiration(v)}
            disabled={saving}
            style={{ display: "block", width: "100%", marginTop: 2 }}
          />
        </label>
        <label
          style={{
            fontSize: 11,
            color: "var(--fg-muted)",
            minWidth: 0,
            gridColumn: "1 / -1",
          }}
        >
          Notes (optional)
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g., LLC name, property address, mortgagee"
            disabled={saving}
            style={{ display: "block", width: "100%", marginTop: 2 }}
          />
        </label>
        <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
          <PersonSelect
            label="For whom? (Insured)"
            members={householdContacts}
            selected={forContactIds}
            onChange={setForContactIds}
            disabled={saving}
            onAddPerson={onAddPerson}
          />
        </div>
      </div>
      {error && (
        <div style={{ color: "var(--danger)", fontSize: 11, marginTop: 6 }} role="alert">
          {error}
        </div>
      )}
      {duplicateWarning && (
        <DuplicatePolicyNumberWarning
          message={duplicateWarning.message}
          existing={duplicateWarning.existing}
          busy={saving}
          onAddAnyway={() => void handleSave(true)}
        />
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <Button
          variant="sw-primary"
          onClick={() => void handleSave()}
          disabled={saving || forContactIds.length === 0}
          style={{ fontSize: 12, padding: "4px 12px" }}
        >
          {saving ? "Saving…" : "Save policy"}
        </Button>
        <Button
          variant="sw-default"
          onClick={() => {
            reset();
            setOpen(false);
            onCancel?.();
          }}
          disabled={saving}
          style={{ fontSize: 12, padding: "4px 12px" }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
